import { useState } from "react";
import { Pencil } from "lucide-react";
import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import {
  Button,
  StateMark,
  SubTable,
  Surface,
  TableValue,
  Timestamp,
  type CellDoor,
} from "@/design-system/components/ui";
import { DetailSection } from "../detailBits";
import { DILIGENCE_ITEMS, type DiligenceItem } from "./diligenceChecklist";
import { RecordFinancialsReceivedModal } from "./RecordFinancialsReceived";

/**
 * What the listing broker owes us, and whether it has arrived.
 *
 * ============================================================================
 * 🔴 A CHECKLIST THAT STORES NO FILES
 * ============================================================================
 *
 * <p>There is no upload target here, no thumbnail, and nowhere for a file to
 * land. This card tracks RECEIPT — a date and who is waiting on what — and the
 * paper itself lives in the submission's Documents tab, which is the one place
 * it lives.</p>
 *
 * <p>That is a deliberate refusal of the obvious feature. A second filing path
 * means two places holding "the financials", and the way that ends is one of
 * them going stale while somebody reads the other. The Documents tab already has
 * the folder rules, the permissions, the versioning and the audit trail; a
 * row-local store here would have none of them and would still look
 * authoritative.</p>
 *
 * ============================================================================
 * 🔴 EACH ROW'S DOOR IS THE ONE THAT CAN ACTUALLY OPEN
 * ============================================================================
 *
 * <p>The two rows are set by different mechanisms and the card says which.
 * <b>Financials</b> is a manual stamp: the row's control writes the date, so it
 * is a button. <b>The CIM</b> is DERIVED — moving the status to "Info received"
 * stamps it server-side and there is no CIM endpoint to call — so its row
 * carries prose naming the transition and no control at all. A checkbox there
 * would either silently no-op or write a value the next recomputation discards,
 * which teaches a reader the surface lies.</p>
 *
 * <p>The status control that DOES set it is in the record header, visible from
 * this tab, so the prose points at something already on screen rather than
 * sending anybody hunting.</p>
 */
export function DiligenceChecklistCard({
  s,
  canWrite,
  onOpenDocuments,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
  /**
   * Takes the reader to where the paper actually is.
   *
   * <p>OPTIONAL, and the fallback is prose rather than a dead button. This body
   * renders inside a record drawer as well as a page, so it reads no router
   * hook and cannot navigate on its own — a host that has not wired the jump
   * gets a sentence naming the tab, which is still true, instead of a control
   * that goes nowhere.</p>
   */
  onOpenDocuments?: () => void;
}) {
  const [recordingFinancials, setRecordingFinancials] = useState(false);

  const rows = DILIGENCE_ITEMS.map((item) => ({
    item,
    receivedAt: item.receivedAt(s) ?? null,
  }));

  const outstanding = rows.filter((r) => !r.receivedAt).length;

  return (
    <DetailSection
      title="Diligence documents"
      action={
        // C4: the date-correction form used to sit here permanently expanded,
        // occupying roughly half the card to serve a case that arises once per
        // submission at most. It is behind the pencil now — which is what the
        // pencil is for — and the unstamped row keeps its own door, so the
        // ordinary path is unchanged and one click shorter than the exception.
        canWrite && s.financialsReceivedAtUtc ? (
          <Button
            variant="text"
            size="sm"
            aria-label="Edit Diligence documents"
            data-testid="diligence-edit"
            onClick={() => setRecordingFinancials(true)}
          >
            <Pencil size={14} aria-hidden />
          </Button>
        ) : null
      }
    >
      <div className="grid gap-space-3">
        <Surface kind="card">
          <SubTable
            caption="Diligence documents"
            data-testid="diligence-checklist"
            rowKey={(r) => r.item.key}
            rowTestId={(r) => `diligence-${r.item.key}`}
            columns={[
              {
                key: "item",
                header: "Item",
                render: (r) => (
                  <span className="grid gap-space-1">
                    <span className="text-small text-onyx">{r.item.label}</span>
                    <span className="text-caption text-gray-mid">{r.item.what}</span>
                  </span>
                ),
              },
              {
                key: "state",
                header: "State",
                render: (r) => (
                  <StateMark
                    present={!!r.receivedAt}
                    presentLabel="Received"
                    absentLabel="Outstanding"
                  />
                ),
              },
              {
                key: "received",
                header: "Received",
                render: (r) => (
                  <TableValue
                    value={r.receivedAt ? <Timestamp value={r.receivedAt} /> : null}
                    door={doorFor(r.item, canWrite, () => setRecordingFinancials(true))}
                  />
                ),
              },
            ]}
            rows={rows}
          />
        </Surface>

        {/* The card's one link out, and the answer to "where do I put the PDF". */}
        <p className="text-caption text-gray-mid m-0">
          {outstanding === 0
            ? "Everything expected has arrived. "
            : `${outstanding} of ${rows.length} still outstanding. `}
          The documents themselves are filed in{" "}
          {onOpenDocuments ? (
            <Button
              variant="text"
              size="sm"
              className="px-0 align-baseline"
              data-testid="diligence-open-documents"
              onClick={onOpenDocuments}
            >
              Documents
            </Button>
          ) : (
            "the Documents tab"
          )}
          .
        </p>
      </div>

      {/* Top level of the section — never inside collapsible content. */}
      <RecordFinancialsReceivedModal
        s={s}
        open={recordingFinancials}
        onClose={() => setRecordingFinancials(false)}
      />
    </DetailSection>
  );
}

/**
 * The door an outstanding row is allowed to carry, from the kind the catalogue
 * declared.
 *
 * <p>🔴 The `derived` branch returns a door with NO `onClick`, which the shared
 * control renders as prose. That is the whole guarantee: a derived row cannot
 * grow a button by accident, because there is no path through this function that
 * gives it one.</p>
 */
function doorFor(
  item: DiligenceItem,
  canWrite: boolean,
  openFinancials: () => void,
): CellDoor {
  if (item.kind === "derived") {
    return { kind: "derived", explain: item.setBy ?? "Set elsewhere." };
  }
  return {
    kind: "stamp",
    label: "Record the date it arrived",
    onClick: openFinancials,
    disabled: !canWrite,
    disabledReason: "Outstanding",
  };
}
